import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mutator } from '@/shared/api';
import { handleSuccess } from '@/shared/lib/success';
import { policyKeys } from './queryKeys';
import { userKeys } from '@/entities/user';
import type {
  CreatePolicyPayload,
  UpdatePolicyPayload,
  PublishPolicyPayload,
  UpsertPolicyDocumentPayload,
  DocumentType,
  PolicyDetailResponse,
  PolicyActionResponse,
} from '../model/types';

export function useSavePolicyDraft(documentType: DocumentType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpsertPolicyDocumentPayload) =>
      mutator<PolicyActionResponse>(
        'PUT',
        `/api/v2/system/policies/${documentType}/draft`,
        payload,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyKeys.lists() });
    },
    onError: () => undefined,
  });
}

export function usePublishPolicyDocument(documentType: DocumentType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpsertPolicyDocumentPayload) =>
      mutator<PolicyDetailResponse>(
        'POST',
        `/api/v2/system/policies/${documentType}/publish`,
        payload,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.profile });
    },
    onError: () => undefined,
  });
}

/**
 * Accept the current privacy policy
 * Uses merchant-prefixed endpoint: POST /merchant/policies/current/accept
 * Sends no body - user comes from token, version resolved server-side
 * Invalidates user profile and current policy on success
 * 
 * **Validates: Requirements 3, 18**
 */
export function useAcceptPolicy(documentType: DocumentType = 'PRIVACY_POLICY') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      mutator(
        'POST',
        `/api/v2/merchant/policies/current/accept?documentType=${documentType}`,
        {},
      ),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: 'success.policy.accepted' });
      queryClient.invalidateQueries({ queryKey: userKeys.profile });
      queryClient.invalidateQueries({ queryKey: policyKeys.current(documentType) });
    },
  });
}

/** Accept every outstanding platform legal document in one request. */
export function useAcceptAllPolicies() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      mutator('POST', '/api/v2/merchant/policies/accept-all', {}),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: 'success.policy.policiesAccepted' });
      queryClient.invalidateQueries({ queryKey: userKeys.profile });
      queryClient.invalidateQueries({ queryKey: policyKeys.all });
    },
  });
}

/**
 * Create a new draft policy
 * Invalidates policy lists on success
 * 
 * **Validates: Requirements 7, 18**
 */
export function useCreatePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePolicyPayload) =>
      mutator('POST', '/api/v2/system/policies', payload),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: 'success.policy.created' });
      queryClient.invalidateQueries({ queryKey: policyKeys.lists() });
    },
  });
}

/**
 * Update an existing draft policy
 * Invalidates policy detail and lists on success
 * 
 * **Validates: Requirements 8, 18**
 */
export function useUpdatePolicy(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdatePolicyPayload) =>
      mutator('PATCH', `/api/v2/system/policies/${id}`, payload),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: 'success.policy.updated' });
      queryClient.invalidateQueries({ queryKey: policyKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: policyKeys.lists() });
    },
  });
}

/**
 * Publish a draft policy
 * Invalidates all policy queries and user profile on success
 * 
 * **Validates: Requirements 9, 18**
 */
export function usePublishPolicy(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PublishPolicyPayload) =>
      mutator('POST', `/api/v2/system/policies/${id}/publish`, payload),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: 'success.policy.published' });
      queryClient.invalidateQueries({ queryKey: policyKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.profile });
    },
  });
}

/**
 * Delete a draft policy
 * Invalidates policy lists on success
 * 
 * **Validates: Requirements 10, 18**
 */
export function useDeletePolicy(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      mutator<PolicyActionResponse>('DELETE', `/api/v2/system/policies/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyKeys.lists() });
    },
    onError: () => undefined,
  });
}
